/**
 * The proof of transfer's client-side constraints. They mirror the backend's,
 * but they are a courtesy rather than the contract: the backend resolves the
 * content type from the file's magic bytes and stays the authority. A renamed
 * file passes here and is still refused with `415`.
 */
export const ACCEPTED_PROOF_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/heic",
  "application/pdf",
] as const;

/** Matches the backend's cap; a larger file is refused with `413`. */
export const MAX_PROOF_SIZE_MB = 10;
export const MAX_PROOF_SIZE_BYTES = MAX_PROOF_SIZE_MB * 1024 * 1024;

/** `accept` attribute for the file input, so the picker filters up front. */
export const PROOF_ACCEPT_ATTRIBUTE = ACCEPTED_PROOF_MIME_TYPES.join(",");

/** Content types the client can render inline; everything else is downloaded. */
const PREVIEWABLE_MIME_TYPES = ["image/jpeg", "image/png"];

/**
 * HEIC renders in no browser and a blob-backed PDF in an `<iframe>` behaves
 * inconsistently across them, so both are handed to the browser as downloads.
 */
export const isPreviewableProof = (contentType: string | null): boolean =>
  !!contentType && PREVIEWABLE_MIME_TYPES.includes(contentType.split(";")[0]);

/** Returns the i18n key of the refusal, or `null` when the file is acceptable. */
export const validateProofFile = (file: File): string | null => {
  if (!ACCEPTED_PROOF_MIME_TYPES.includes(file.type as never)) {
    return "payments.proof.errors.unsupportedType";
  }
  if (file.size > MAX_PROOF_SIZE_BYTES) {
    return "payments.proof.errors.tooLarge";
  }
  return null;
};

export const formatFileSize = (bytes: number): string => {
  const megabytes = bytes / (1024 * 1024);
  return megabytes >= 1
    ? `${megabytes.toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};
